import { KeyboardEvent, useCallback, useRef } from 'react'
import cx from 'classnames'
import {
  useVerticalDrag,
  useWheelAdjust,
} from '../../hooks/useDragAdjust'
import styles from './RotaryKnob.module.css'
import { arcPath, polarPoint } from '~m/designSystem'
import { clamp } from '@pogokid/util'

/* The dial sweeps 270 degrees, from 7 o'clock to 5 o'clock */
const SWEEP_START = -135
const SWEEP_END = 135

/* Full drag travel in pixels for min-to-max */
const DRAG_RANGE_PX = 200

const TICK_COUNT = 10

export type RotaryKnobVariant = 'default' | 'info'

export interface RotaryKnobProps {
  /** Label shown under the dial */
  label: string
  /** Current value */
  value: number
  /** Called with the new value when the user adjusts the dial */
  onChange: (value: number) => void
  /** Minimum value */
  min?: number
  /** Maximum value */
  max?: number
  /** Decimal places shown in the value readout */
  decimals?: number
  /** Map the slider position exponentially (for frequencies) */
  log?: boolean
  /** Format the value in the readout */
  format?: (value: number) => string
  /** Colour concept: 'info' recolours the arc cyan for time-based effects */
  variant?: RotaryKnobVariant
  /** Additional CSS class */
  className?: string
}

/**
 * A rotary dial control styled like an FX pedal knob.
 * Adjust by dragging vertically, scrolling, or with the
 * arrow keys when focused.
 */
export function RotaryKnob({
  label,
  value,
  onChange,
  min = 0,
  max = 10,
  decimals = 0,
  log = false,
  format = (v) => v.toFixed(decimals),
  variant = 'default',
  className,
}: RotaryKnobProps) {
  const dialRef = useRef<HTMLDivElement>(null)

  // Format the current value for UI
  const displayValue = format(value)
  const ariaValue = Number(value.toFixed(decimals))

  const handleChange = useCallback(
    (linear: number) => {
      onChange(log ? min * (max / min) ** linear : linear)
    },
    [log, max, min, onChange]
  )

  // use the internal linear value independent of how it is
  // mapped to the resulting value (ie log)
  const linearMin = log ? 0 : min
  const linearMax = log ? 1 : max
  const linearValue = log
    ? Math.log(value / min) / Math.log(max / min)
    : value

  // fractional position (0 => 1) of the value between min and max
  // ie (15 - 10) / (20 - 10) is 50% = 0.5
  const position =
    (linearValue - linearMin) / (linearMax - linearMin)
  const degrees =
    SWEEP_START + (SWEEP_END - SWEEP_START) * position

  const nudge = useCallback(
    (deltaPx: number) => {
      handleChange(
        clamp(
          linearValue +
            (linearMax - linearMin) * (deltaPx / DRAG_RANGE_PX),
          linearMin,
          linearMax
        )
      )
    },
    [handleChange, linearMax, linearMin, linearValue]
  )

  const dragHandlers = useVerticalDrag(nudge)
  useWheelAdjust(dialRef, (direction) => nudge(direction * 8))

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = (linearMax - linearMin) / 50
    let next: number
    if (event.key === 'ArrowUp' || event.key === 'ArrowRight') {
      next = clamp(linearValue + step, linearMin, linearMax)
    } else if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowLeft'
    ) {
      next = clamp(linearValue - step, linearMin, linearMax)
    } else if (event.key === 'Home') {
      next = linearMin
    } else if (event.key === 'End') {
      next = linearMax
    } else {
      return
    }
    event.preventDefault()
    handleChange(next)
  }

  const ticks = Array.from(
    { length: TICK_COUNT + 1 },
    (_, index) => {
      const tickDegrees =
        SWEEP_START +
        ((SWEEP_END - SWEEP_START) * index) / TICK_COUNT
      const outer = polarPoint(48, 48, 44, tickDegrees)
      const inner = polarPoint(48, 48, 40, tickDegrees)
      return (
        <line
          key={index}
          className={styles.RotaryKnob__tick}
          x1={outer[0]}
          y1={outer[1]}
          x2={inner[0]}
          y2={inner[1]}
        />
      )
    }
  )

  return (
    <div
      className={cx(styles.RotaryKnob, className, {
        [styles.isVariantInfo]: variant === 'info',
      })}
    >
      <div className={styles.RotaryKnob__stage}>
        <svg
          className={styles.RotaryKnob__scale}
          viewBox="0 0 96 96"
          aria-hidden="true"
        >
          {ticks}
          <path
            className={styles.RotaryKnob__track}
            d={arcPath(48, 48, 36, SWEEP_START, SWEEP_END)}
          />
          {position > 0.001 && (
            <path
              className={styles.RotaryKnob__arc}
              d={arcPath(48, 48, 36, SWEEP_START, degrees)}
            />
          )}
        </svg>
        <div
          ref={dialRef}
          className={styles.RotaryKnob__dial}
          style={{ transform: `rotate(${degrees}deg)` }}
          role="slider"
          tabIndex={0}
          aria-label={label}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={ariaValue}
          aria-valuetext={displayValue}
          onKeyDown={onKeyDown}
          {...dragHandlers}
        >
          <div className={styles.RotaryKnob__pointer} />
        </div>
      </div>
      <span className={styles.RotaryKnob__label}>{label}</span>
      <span className={styles.RotaryKnob__value}>
        {displayValue}
      </span>
    </div>
  )
}
